import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FollowUpMenuItem, FollowUpSelection } from '@models/follow-up-options.model';
import { NgSelectModule } from '@ng-select/ng-select';
import { FollowUpOptionsService } from '@services/api/follow-up-options.service';
import moment from 'moment';
import { CalendarModule } from 'primeng/calendar';
import { DialogModule } from 'primeng/dialog';

/** `data.contact_requirement` on update-lead's 422 (REM-3537_NO_RESPONSE_AND_ACTION_TODAY_FRONTEND.md §1). */
export interface ContactRequirement {
  met: boolean;
  touchpoints: number;
  required: number;
  remaining: number;
  /** Every touchpoint, however far apart — there is no 14-day window (REM-3537_2026-10-14 §12, 16 Oct). */
  by_channel?: { call?: number; sms?: number; email?: number };
  follow_up_completed: boolean;
}

/**
 * A follow-up booked from the dialog: either one of the agent's presets, or a picked date
 * (`YYYY-MM-DD`) and time. `label` is what the dialog shows once the host has saved it.
 */
export interface NoResponseFollowUpChoice {
  selection?: FollowUpSelection;
  date?: string;
  time?: string;
  label: string;
}

/**
 * Why a lead could not be moved to No Response — shown in place of the raw error toast. The client
 * asked for a readable box: what is still missing, how far the lead has got, and which status it
 * stays on, rather than the backend's one long sentence repeated per auto-save attempt.
 *
 * The Follow-up step can be acted on in place: the client wanted to book the missing follow-up from
 * this box instead of closing it and finding the flag button. The dialog only collects the choice;
 * the host creates the task (the same request its own Follow Up menu sends) and reports back.
 */
@Component({
  selector: 'app-no-response-blocked-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule, CalendarModule, NgSelectModule],
  templateUrl: './no-response-blocked-dialog.component.html',
  styleUrls: ['./no-response-blocked-dialog.component.scss'],
})
export class NoResponseBlockedDialogComponent implements OnChanges {
  @Input() visible: boolean = false;
  @Input() blockedStatus: string = null;
  @Input() keptStatus: string = null;
  @Input() requirement: ContactRequirement = null;
  /** The host's task time list (`{ label, value }`), the same one its own Follow Up menu offers. */
  @Input() timerOptions: any[] = [];
  /** False until the host has the task type/status/priority a follow-up needs. */
  @Input() canAddFollowUp: boolean = false;
  @Input() isFollowUpSaving: boolean = false;
  /** Set by the host once the follow-up it was handed has been saved. */
  @Input() bookedFollowUpLabel: string = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() addFollowUp = new EventEmitter<NoResponseFollowUpChoice>();

  isFollowUpPickerOpen = false;
  followUpOptions: FollowUpMenuItem[] = [];
  pickedDate: Date = null;
  pickedTime: string = null;
  readonly today = new Date();

  constructor(private followUpOptionsService: FollowUpOptionsService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.isFollowUpPickerOpen = false;
      this.pickedDate = null;
      this.pickedTime = null;
    }
    if (changes['bookedFollowUpLabel'] && this.bookedFollowUpLabel) {
      this.isFollowUpPickerOpen = false;
    }
  }

  get touchpoints(): number {
    return this.requirement?.touchpoints || 0;
  }

  /**
   * How many touchpoints this lead needs — 9 for most, 3 for a Reassigned (Old Lead) one
   * (REM-3537_2026-10-14 §13). Always the backend's number; 0 only if it ever left it out.
   */
  get required(): number {
    return this.requirement?.required || 0;
  }

  get remaining(): number {
    if (this.requirement?.remaining != null) {
      return this.requirement.remaining;
    }
    return this.required ? Math.max(0, this.required - this.touchpoints) : null;
  }

  get hasEnoughTouchpoints(): boolean {
    return this.remaining === 0;
  }

  get progressPercent(): number {
    return this.required ? Math.min(100, Math.round((this.touchpoints / this.required) * 100)) : 0;
  }

  get channels(): { label: string; count: number }[] {
    const byChannel = this.requirement?.by_channel || {};
    return [
      { label: (byChannel.call || 0) === 1 ? 'call' : 'calls', count: byChannel.call || 0 },
      { label: 'SMS', count: byChannel.sms || 0 },
      { label: (byChannel.email || 0) === 1 ? 'email' : 'emails', count: byChannel.email || 0 },
    ];
  }

  /** The tile is only an action while there is a follow-up still to book. */
  get canBookFollowUp(): boolean {
    return !this.requirement?.follow_up_completed && !this.bookedFollowUpLabel;
  }

  toggleFollowUpPicker(): void {
    if (!this.canBookFollowUp) {
      return;
    }
    this.isFollowUpPickerOpen = !this.isFollowUpPickerOpen;
    if (this.isFollowUpPickerOpen) {
      // Times are relative to now ("Later Today"), so the list is built on open, not once.
      this.followUpOptionsService.ensureLoaded();
      this.followUpOptions = this.followUpOptionsService.menuItems();
    }
  }

  pickPreset(item: FollowUpMenuItem): void {
    const selection = this.followUpOptionsService.selectionFor(item.id);
    if (!selection || !this.canAddFollowUp || this.isFollowUpSaving) {
      return;
    }
    this.addFollowUp.emit({ selection, label: `${item.label} · ${item.display}` });
  }

  savePickedDate(): void {
    if (!this.pickedDate || !this.pickedTime || !this.canAddFollowUp || this.isFollowUpSaving) {
      return;
    }
    const timeLabel = this.timerOptions?.find(option => option?.value === this.pickedTime)?.label || this.pickedTime;
    this.addFollowUp.emit({
      date: moment(this.pickedDate).format('YYYY-MM-DD'),
      time: this.pickedTime,
      label: `${moment(this.pickedDate).format('ddd D MMM')}, ${timeLabel}`,
    });
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }
}
